import React, { useState, useEffect, useMemo, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from './common/SafeIcon'
import { useSimpleAuth } from './hooks/useSimpleAuth'
import { usePeople, useRelationships } from './hooks/useData'
import LoginPage from './components/LoginPage'
import UserProfile from './components/UserProfile'
import PersonCard from './components/PersonCard'
import PersonForm from './components/PersonForm'
import FullscreenPersonCard from './components/FullscreenPersonCard'
import SearchBar from './components/SearchBar'
import DeleteConfirmation from './components/DeleteConfirmation'
import FamilyTreeView from './components/FamilyTreeView'
import PrintableFamilyTree from './components/PrintableFamilyTree'
import { useTheme, ThemeProvider } from './context/ThemeContext'
import './App.css'

const { FiUsers, FiPlus, FiGrid, FiShare2, FiCheckCircle } = FiIcons

function MainApp() {
  const { user, loading: authLoading, signOut } = useSimpleAuth()
  const { people, loading, fetchPeople, addPerson, updatePerson, deletePerson } = usePeople()
  const { relationships: allRelationships, fetchRelationships, addRelationship, updateRelationship, deleteRelationship, cleanupDatabase, syncAllFamilyRelationships, deleteAllRelationships } = useRelationships()
  const { theme } = useTheme()

  const [viewMode, setViewMode] = useState('grid')
  const [focusedTreePersonId, setFocusedTreePersonId] = useState(null)
  const [showPersonForm, setShowPersonForm] = useState(false)
  const [editingPerson, setEditingPerson] = useState(null)
  const [selectedPerson, setSelectedPerson] = useState(null)
  const [initialFullscreenConfig, setInitialFullscreenConfig] = useState({})
  const [initialEditingRel, setInitialEditingRel] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showToast, setShowToast] = useState(null)
  const [showPrintTree, setShowPrintTree] = useState(false)

  // Alphabet Scroll States
  const [isScrolling, setIsScrolling] = useState(false)
  const [activeLetter, setActiveLetter] = useState('')
  const scrollTimeoutRef = useRef(null)

  // Deletion state
  const [personToDelete, setPersonToDelete] = useState(null)
  const [relToDelete, setRelToDelete] = useState(null)
  const [confirmDeleteAllRels, setConfirmDeleteAllRels] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (user) {
      fetchPeople()
      fetchRelationships()
    }
  }, [user, fetchPeople, fetchRelationships])

  const sortedFilteredPeople = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return [...people].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return people
      .filter(p => {
        return (
          p.name?.toLowerCase().includes(q) ||
          p.nicknames?.toLowerCase().includes(q) ||
          p.notes?.toLowerCase().includes(q) ||
          p.proximity?.toLowerCase().includes(q) ||
          p.sex?.toLowerCase().includes(q) ||
          p.dob?.toLowerCase().includes(q) ||
          p.facebook_url?.toLowerCase().includes(q) ||
          p.linkedin_url?.toLowerCase().includes(q) ||
          p.twitter_url?.toLowerCase().includes(q) ||
          p.instagram_url?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [people, searchQuery])

  const handleViewModeChange = (mode, personId = null) => {
    setViewMode(mode)
    if (mode === 'tree') {
      setFocusedTreePersonId(personId)
    } else {
      setFocusedTreePersonId(null)
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (viewMode !== 'grid') return;
      
      setIsScrolling(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1500);

      const personElements = document.querySelectorAll('[id^="person-"]');
      let firstVisible = null;
      for (let el of personElements) {
        const rect = el.getBoundingClientRect();
        if (rect.top >= 150 && rect.top <= window.innerHeight) { 
          firstVisible = el;
          break;
        }
      }

      if (firstVisible) {
        const personId = firstVisible.id.replace('person-', '');
        const person = people.find(p => p.id === personId);
        if (person) {
          const initial = (person.name || '#').charAt(0).toUpperCase();
          setActiveLetter(/[A-Z]/.test(initial) ? initial : '#');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [viewMode, people]);

  const handlePersonSubmit = async (personData, preventClose = false) => {
    const { newRelationships, ...data } = personData
    let saved
    let totalSyncAdded = 0;

    try {
      if (editingPerson) {
        saved = await updatePerson(editingPerson.id, data)
      } else {
        saved = await addPerson(data)
      }

      // Update selectedPerson if it's the one we just edited
      if (selectedPerson && saved && saved.id === selectedPerson.id) {
        setSelectedPerson(prev => ({ ...prev, ...saved }));
      }

      if (newRelationships?.length > 0) {
        for (const rel of newRelationships) {
          const res = await addRelationship(saved.id, rel.relatedPersonId, rel.relationshipType, [...people, saved], allRelationships);
          totalSyncAdded += (res.addedCount || 0);
        }
      }

      if (totalSyncAdded > 0) {
        setShowToast({ type: 'success', message: `Sync Complete: ${totalSyncAdded} new connections added.` })
        setTimeout(() => setShowToast(null), 4000);
      }

      if (!preventClose) {
        setShowPersonForm(false)
        setEditingPerson(null)
        
        // Return to the person in the grid if we're not in fullscreen mode
        if (!selectedPerson && saved && viewMode === 'grid') {
          setTimeout(() => {
            const target = document.getElementById(`person-${saved.id}`); 
            if (target) {
              const header = document.querySelector('.sticky.top-0');
              const headerOffset = header ? header.offsetHeight + 24 : 180;
              const elementPosition = target.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'auto'
              });
            }
          }, 100);
        }
      }

      fetchPeople()
      fetchRelationships()
      return saved;
    } catch (error) {
      console.error("Submit error:", error);
      setShowToast({ type: 'error', message: "Failed to save: " + error.message })
      setTimeout(() => setShowToast(null), 3000)
      throw error;
    }
  }

  const handleViewRelationships = (person, config = {}) => {
    setSearchQuery('');
    setSelectedPerson(person);
    setInitialFullscreenConfig(config);
  }

  if (authLoading) return <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center transition-colors duration-300"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
  if (!user) return <LoginPage onAuthSuccess={() => { }} />

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300 relative">
      <AnimatePresence>
        {isScrolling && activeLetter && viewMode === 'grid' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.5, x: 20 }}
            className="fixed right-6 md:right-16 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
          >
            <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold shadow-2xl border-4 border-white dark:border-slate-800 transition-colors">
              {activeLetter}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 pb-8">
        <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-slate-950/95 backdrop-blur-sm pt-8 pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 mb-4 border-b border-gray-200/50 dark:border-slate-800/50 shadow-sm transition-colors duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiUsers} className="text-3xl text-blue-600 dark:text-blue-500" />
              <h1 className="text-3xl font-bold text-gray-800 dark:text-white transition-colors">Friends Finder</h1>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => { setEditingPerson(null); setShowPersonForm(true) }} className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2 shadow-md transition-colors"><SafeIcon icon={FiPlus} /><span>Add Person</span></button>
              <UserProfile 
                user={user} 
                onSignOut={signOut} 
                onFamilySync={() => syncAllFamilyRelationships(people, allRelationships)} 
                onDebugSync={() => {}}
                onCleanupDatabase={() => cleanupDatabase(people)} 
                onDeleteAllRelationships={() => setConfirmDeleteAllRels(true)}
                onPrintTree={() => setShowPrintTree(true)}
              />
            </div>
          </div>

          <SearchBar query={searchQuery} onQueryChange={setSearchQuery} />
        </div>

        <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start" : ""}>
          {viewMode === 'grid' ? (
            sortedFilteredPeople.map(p => (
              <PersonCard
                key={p.id}
                person={p}
                people={people}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                relationships={allRelationships.filter(r => r.person_a_id === p.id || r.person_b_id === p.id)}
                onEdit={(person) => { setEditingPerson(person); setShowPersonForm(true) }}
                onDelete={(person) => setPersonToDelete(person)}
                onViewRelationships={handleViewRelationships}
                onNavigateToPerson={(id) => { 
                  setSearchQuery('');
                  setTimeout(() => {
                    const target = document.getElementById(`person-${id}`); 
                    if (target) {
                      const header = document.querySelector('.sticky.top-0');
                      const headerOffset = header ? header.offsetHeight + 24 : 180;
                      const elementPosition = target.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                      
                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'auto'
                      });
                    }
                  }, 50);
                }}
                onDeleteRelationship={(id) => setRelToDelete(id)}
                onEditRelationship={(rel, currentPerson) => { 
                   setInitialEditingRel(rel);
                   setSelectedPerson(currentPerson);
                }}
              />
            ))
          ) : (
            <FamilyTreeView 
              people={people} 
              relationships={allRelationships} 
              onPersonClick={(person) => {
                setSearchQuery('');
                setSelectedPerson(person);
              }} 
              focusedPersonId={focusedTreePersonId}
              onClose={() => handleViewModeChange('grid')}
            />
          )}
        </div>
      </div>

      <AnimatePresence>
        {showPersonForm && (
          <motion.div key="person-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
            <PersonForm 
              person={editingPerson} 
              people={people} 
              onSubmit={handlePersonSubmit} 
              onCancel={() => setShowPersonForm(false)} 
              onViewRelationships={handleViewRelationships}
            />
          </motion.div>
        )}
        {selectedPerson && (
          <FullscreenPersonCard
            key="fullscreen-person-card"
            person={selectedPerson}
            people={people}
            allRelationships={allRelationships}
            initialEditingRelationship={initialEditingRel}
            initialShowAddForm={initialFullscreenConfig.showAddForm}
            onClose={() => {
              setSelectedPerson(null);
              setInitialEditingRel(null);
              setInitialFullscreenConfig({});
            }}
            onNavigate={(person) => {
              setSearchQuery('');
              setSelectedPerson(person);
            }}
            onAddPerson={async (name, sex) => { const newPerson = await addPerson({ name, sex, proximity: 'Medium' }); return newPerson }}
            onAddRelationships={async (id, data) => {
              const res = await addRelationship(id, data[0].relatedPersonId, data[0].relationshipType, people, allRelationships);
              fetchRelationships();
              return res
            }}
            onUpdateRelationship={async (id, data, currentPersonId, allPeople) => {
              const res = await updateRelationship(id, data, currentPersonId, allPeople);
              fetchRelationships();
              return res;
            }}
            onDeleteRelationship={async (id) => { await deleteRelationship(id); fetchRelationships() }}
            onEditPerson={(person) => { setEditingPerson(person); setShowPersonForm(true) }}
            onDeletePerson={(person) => setPersonToDelete(person)}
          />
        )}
        {personToDelete && (
          <DeleteConfirmation key="delete-person" title={`Delete ${personToDelete.name}?`} message="This will permanently remove this person and all their connected relationships." loading={isDeleting} onConfirm={() => deletePerson(personToDelete.id).then(() => setPersonToDelete(null))} onCancel={() => setPersonToDelete(null)} />
        )}
        {relToDelete && (
          <DeleteConfirmation key="delete-rel" title="Delete Relationship?" message="Are you sure you want to remove this connection?" loading={isDeleting} onConfirm={() => deleteRelationship(relToDelete).then(() => setRelToDelete(null))} onCancel={() => setRelToDelete(null)} />
        )}
        {confirmDeleteAllRels && (
          <DeleteConfirmation key="delete-all-rels" title="Delete All Relationships?" message="Are you sure you want to delete ALL relationships?" loading={isDeleting} onConfirm={() => deleteAllRelationships().then(() => setConfirmDeleteAllRels(false))} onCancel={() => setConfirmDeleteAllRels(false)} />
        )}
        {showPrintTree && (
          <PrintableFamilyTree
            key="print-tree"
            people={people}
            relationships={allRelationships}
            onClose={() => setShowPrintTree(false)}
          />
        )}
        {showToast && (
          <motion.div key="toast" initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-slate-800 text-white px-6 py-3 rounded-full flex items-center space-x-3 shadow-2xl z-[300] transition-colors">
            <SafeIcon icon={FiCheckCircle} className="text-green-400" />
            <span>{showToast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <MainApp />
    </ThemeProvider>
  )
}

export default App